const VOICEFLOW_WIDGET_SRC = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';
const VOICEFLOW_RUNTIME_URL = 'https://general-runtime.voiceflow.com';
const VOICEFLOW_VOICE_URL = 'https://runtime-api.voiceflow.com';

let voiceflowScriptLoadingPromise = null;

export const getVoiceflowWidgetSource = () => VOICEFLOW_WIDGET_SRC;
export const getVoiceflowRuntimeUrl = () => VOICEFLOW_RUNTIME_URL;
export const getVoiceflowVoiceUrl = () => VOICEFLOW_VOICE_URL;

export const loadVoiceflowScript = () => {
    if (window.voiceflow?.chat) {
        return Promise.resolve();
    }

    if (voiceflowScriptLoadingPromise !== null) {
        return voiceflowScriptLoadingPromise;
    }

    voiceflowScriptLoadingPromise = new Promise((resolve, reject) => {
        const handleScriptLoaded = (event) => {
            resolve(event);
        };
        const handleScriptError = (error) => {
            reject(error);
        };
        const existingScript = document.querySelector(`script[src="${VOICEFLOW_WIDGET_SRC}"]`);
        if (existingScript) {
            const isScriptLoaded = existingScript.getAttribute('data-utr-voiceflow-loaded') === 'true' || window.voiceflow?.chat;
            if (isScriptLoaded) {
                resolve();
                return;
            }
            existingScript.addEventListener('load', handleScriptLoaded);
            existingScript.addEventListener('error', handleScriptError);
            return;
        }

        const scriptTag = document.createElement('script');
        const firstScript = document.getElementsByTagName('script')[0];

        scriptTag.onload = (event) => {
            scriptTag.setAttribute('data-utr-voiceflow-loaded', 'true');
            handleScriptLoaded(event);
        };
        scriptTag.onerror = handleScriptError;
        scriptTag.src = VOICEFLOW_WIDGET_SRC;
        scriptTag.type = 'text/javascript';

        firstScript.parentNode.insertBefore(scriptTag, firstScript);
    });

    voiceflowScriptLoadingPromise = voiceflowScriptLoadingPromise.catch((error) => {
        voiceflowScriptLoadingPromise = null;
        throw error;
    });

    return voiceflowScriptLoadingPromise;
};

export const parseVoiceflowWidgetMessage = (messageData) => {
    if (!messageData) {
        return null;
    }

    if (typeof messageData === 'string') {
        try {
            return JSON.parse(messageData);
        } catch (error) {
            if (!(error instanceof SyntaxError)) {
                // eslint-disable-next-line no-console
                console.warn('Unexpected Voiceflow widget parse error', error);
            }
            return null;
        }
    }

    return messageData;
};

export const isVoiceflowCloseEvent = (messageData) => {
    const parsedMessage = parseVoiceflowWidgetMessage(messageData);
    return parsedMessage?.type === 'voiceflow:close';
};

export const shouldCloseFromVoiceflowMessage = (messageData) => {
    const parsedMessage = parseVoiceflowWidgetMessage(messageData);
    const isMalformed = typeof messageData === 'string' && !parsedMessage;
    return {
        isMalformed,
        shouldClose: !isMalformed && parsedMessage?.type === 'voiceflow:close',
    };
};

export const cleanupVoiceflowObserver = (observer) => {
    if (observer) {
        observer.disconnect();
    }
    return null;
};

const SPARKLE_PATH = 'M6.58973 3.9352C6.77063 3.90595 6.86601 3.80702 6.87589 3.63841C6.88577 3.46979 6.80261 3.36041 6.62636 3.31031C6.04684 3.17693 5.59597 3.05605 5.2738 2.94766C4.94172 2.83869 4.69629 2.69497 4.53751 2.5165C4.37873 2.33803 4.2596 2.07728 4.18014 1.73425C4.10068 1.39122 4.01918 0.913699 3.93557 0.301703C3.91685 0.11151 3.81819 0.011201 3.63961 0.000769926C3.47095 -0.00908166 3.36153 0.0741082 3.31139 0.250327C3.1464 0.857754 3.0094 1.32747 2.90036 1.65949C2.78198 1.98102 2.63354 2.22116 2.45501 2.37992C2.26713 2.52819 2.00687 2.6374 1.67425 2.70755C1.34163 2.77769 0.884148 2.85547 0.301764 2.94088C0.110943 2.96954 0.0105979 3.06818 0.000721791 3.2368C-0.00915436 3.40542 0.0789901 3.51507 0.265164 3.56576C0.833605 3.71839 1.28388 3.84919 1.61596 3.95817C1.93871 4.05664 2.17946 4.19511 2.33824 4.37358C2.4976 4.54213 2.61701 4.79792 2.69647 5.14096C2.77593 5.48399 2.85743 5.96151 2.94104 6.5735C2.95977 6.7637 3.05846 6.864 3.23703 6.87443C3.41561 6.88486 3.52525 6.79673 3.56605 6.61002C3.73046 6.01251 3.87217 5.54803 3.99113 5.21659C4.11067 4.87523 4.25943 4.63012 4.43738 4.48127C4.61591 4.3225 4.87118 4.21301 5.20322 4.15278C5.53584 4.08264 5.998 4.01011 6.58973 3.9352Z';

export const injectVoiceflowHeaderTitle = ({
    target,
    retryCount = 0,
    maxRetries = 20,
}) => {
    const shadowRoot = target?.shadowRoot;

    if (!shadowRoot) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderTitle({ target, retryCount: retryCount + 1, maxRetries });
            }, 100);
        }
        return;
    }

    const ensureTitle = () => {
        const titleEl = shadowRoot.querySelector('.vfrc-header--title');
        if (!titleEl) {
            return false;
        }

        if (shadowRoot.getElementById('utr-voiceflow-header-title')) {
            return true;
        }

        const svgNs = 'http://www.w3.org/2000/svg';

        const wrapper = document.createElement('span');
        wrapper.id = 'utr-voiceflow-header-title';
        wrapper.style.fontSize = '16px';
        wrapper.style.color = 'white';
        wrapper.style.display = 'inline-flex';
        wrapper.style.alignItems = 'center';

        const primaryText = document.createElement('span');
        primaryText.style.display = 'inline-flex';
        primaryText.style.alignItems = 'baseline';

        const chatWith = document.createElement('span');
        chatWith.textContent = 'Chat with';
        chatWith.style.fontWeight = 'normal';

        const rally = document.createElement('span');
        rally.textContent = 'Rally';
        rally.style.marginLeft = '4px';
        rally.style.fontWeight = '600';

        primaryText.appendChild(chatWith);
        primaryText.appendChild(rally);

        const starSpan = document.createElement('span');
        starSpan.style.width = '6.58px';
        starSpan.style.height = '6.58px';
        starSpan.style.display = 'inline-flex';
        starSpan.style.alignItems = 'center';
        starSpan.style.justifyContent = 'center';
        starSpan.style.position = 'relative';
        starSpan.style.top = '-5px';
        starSpan.style.marginLeft = '0';
        starSpan.style.marginRight = '2px';
        starSpan.style.flexShrink = '0';

        const starSvg = document.createElementNS(svgNs, 'svg');
        starSvg.setAttribute('width', '100%');
        starSvg.setAttribute('height', '100%');
        starSvg.setAttribute('viewBox', '0 0 7 7');
        starSvg.setAttribute('fill', 'none');
        starSvg.setAttribute('aria-hidden', 'true');

        const starPath = document.createElementNS(svgNs, 'path');
        starPath.setAttribute('d', SPARKLE_PATH);
        starPath.setAttribute('fill', 'white');

        starSvg.appendChild(starPath);
        starSpan.appendChild(starSvg);
        wrapper.appendChild(primaryText);
        wrapper.appendChild(starSpan);

        titleEl.appendChild(wrapper);
        return true;
    };

    const titleAdded = ensureTitle();
    if (!titleAdded) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderTitle({ target, retryCount: retryCount + 1, maxRetries });
            }, 100);
        }
        return;
    }

    const observer = new MutationObserver(() => {
        ensureTitle();
    });
    observer.observe(shadowRoot, { childList: true, subtree: true });
};

export const injectVoiceflowHeaderCloseButton = ({
    target,
    onClose,
    existingObserver = null,
    setObserver,
    retryCount = 0,
    maxRetries = 20,
}) => {
    const shadowRoot = target?.shadowRoot;

    if (!shadowRoot) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderCloseButton({
                    target,
                    onClose,
                    existingObserver,
                    setObserver,
                    retryCount: retryCount + 1,
                    maxRetries,
                });
            }, 100);
        }
        return existingObserver;
    }

    const ensureButton = () => {
        const header = shadowRoot.querySelector('.vfrc-header');
        if (!header) {
            return false;
        }

        const existingButton = shadowRoot.getElementById('utr-voiceflow-close-button');
        if (existingButton) {
            return true;
        }

        const refreshControl = header.querySelector('[role="button"]') || header.querySelector('button');
        const headerControls = refreshControl?.parentElement || header;

        const closeButton = document.createElement('button');
        closeButton.id = 'utr-voiceflow-close-button';
        closeButton.type = 'button';
        closeButton.setAttribute('aria-label', 'Close chat');
        closeButton.setAttribute('title', 'Close chat');
        closeButton.style.border = '0';
        closeButton.style.background = 'transparent';
        closeButton.style.color = '#ffffff';
        closeButton.style.cursor = 'pointer';
        closeButton.style.marginLeft = '4px';
        closeButton.style.padding = '0';
        closeButton.style.display = 'inline-flex';
        closeButton.style.alignItems = 'center';
        closeButton.style.justifyContent = 'center';
        closeButton.style.borderRadius = '4px';
        closeButton.style.transition = 'background-color 0.15s ease';

        if (refreshControl) {
            const refreshStyles = window.getComputedStyle(refreshControl);
            closeButton.style.width = refreshStyles.width;
            closeButton.style.height = refreshStyles.height;
            closeButton.style.padding = refreshStyles.padding;
            closeButton.style.color = refreshStyles.color;
            closeButton.style.borderRadius = refreshStyles.borderRadius;
        }

        const refreshIcon = refreshControl?.querySelector('svg, span, i');
        const refreshSvg = refreshIcon?.tagName?.toLowerCase() === 'svg'
            ? refreshIcon
            : refreshIcon?.querySelector?.('svg');

        if (refreshSvg) {
            const svgNs = 'http://www.w3.org/2000/svg';
            const closeSvg = refreshSvg.cloneNode(false);
            const viewBox = refreshSvg.getAttribute('viewBox') || '0 0 24 24';
            const viewBoxParts = viewBox.split(' ').map(Number);
            const minX = Number.isFinite(viewBoxParts[0]) ? viewBoxParts[0] : 0;
            const minY = Number.isFinite(viewBoxParts[1]) ? viewBoxParts[1] : 0;
            const width = Number.isFinite(viewBoxParts[2]) ? viewBoxParts[2] : 24;
            const height = Number.isFinite(viewBoxParts[3]) ? viewBoxParts[3] : 24;
            const insetX = width * 0.28;
            const insetY = height * 0.28;

            const strokeNode = refreshSvg.querySelector('[stroke-width]');
            const strokeWidth = strokeNode?.getAttribute('stroke-width') || refreshSvg.getAttribute('stroke-width') || '2';
            const strokeLineCap = strokeNode?.getAttribute('stroke-linecap') || refreshSvg.getAttribute('stroke-linecap') || 'round';
            const strokeLineJoin = strokeNode?.getAttribute('stroke-linejoin') || refreshSvg.getAttribute('stroke-linejoin') || 'round';

            closeSvg.setAttribute('aria-hidden', 'true');
            closeSvg.setAttribute('focusable', 'false');

            const lineOne = document.createElementNS(svgNs, 'line');
            lineOne.setAttribute('x1', String(minX + insetX));
            lineOne.setAttribute('y1', String(minY + insetY));
            lineOne.setAttribute('x2', String(minX + width - insetX));
            lineOne.setAttribute('y2', String(minY + height - insetY));
            lineOne.setAttribute('stroke', 'currentColor');
            lineOne.setAttribute('stroke-width', strokeWidth);
            lineOne.setAttribute('stroke-linecap', strokeLineCap);
            lineOne.setAttribute('stroke-linejoin', strokeLineJoin);

            const lineTwo = document.createElementNS(svgNs, 'line');
            lineTwo.setAttribute('x1', String(minX + width - insetX));
            lineTwo.setAttribute('y1', String(minY + insetY));
            lineTwo.setAttribute('x2', String(minX + insetX));
            lineTwo.setAttribute('y2', String(minY + height - insetY));
            lineTwo.setAttribute('stroke', 'currentColor');
            lineTwo.setAttribute('stroke-width', strokeWidth);
            lineTwo.setAttribute('stroke-linecap', strokeLineCap);
            lineTwo.setAttribute('stroke-linejoin', strokeLineJoin);

            closeSvg.appendChild(lineOne);
            closeSvg.appendChild(lineTwo);
            closeButton.appendChild(closeSvg);
        } else {
            const closeIcon = document.createElement('span');
            closeIcon.textContent = '\u00D7';
            closeIcon.style.fontSize = '18px';
            closeIcon.style.lineHeight = '1';
            closeButton.appendChild(closeIcon);
        }

        closeButton.addEventListener('mouseenter', () => {
            closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.16)';
        });
        closeButton.addEventListener('mouseleave', () => {
            closeButton.style.backgroundColor = 'transparent';
        });

        headerControls.appendChild(closeButton);
        closeButton.addEventListener('click', onClose);
        return true;
    };

    const buttonAdded = ensureButton();
    if (!buttonAdded) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderCloseButton({
                    target,
                    onClose,
                    existingObserver,
                    setObserver,
                    retryCount: retryCount + 1,
                    maxRetries,
                });
            }, 100);
        }
        return existingObserver;
    }

    if (!existingObserver) {
        const observer = new MutationObserver(() => {
            ensureButton();
        });
        observer.observe(shadowRoot, { childList: true, subtree: true });
        if (setObserver) {
            setObserver(observer);
        }
        return observer;
    }

    return existingObserver;
};

export const initializeVoiceflowEmbeddedChat = async ({
    target,
    userId,
    firstName,
    lastName,
    isPowered,
    projectId,
    versionId,
    onClose,
    existingObserver = null,
    setObserver,
}) => {
    await loadVoiceflowScript();

    if (!target) {
        return existingObserver;
    }

    target.innerHTML = '';
    const normalizedUserId = String(userId);

    window.voiceflow.chat.load({
        verify: { projectID: projectId },
        url: VOICEFLOW_RUNTIME_URL,
        versionID: versionId,
        userID: normalizedUserId,
        render: {
            mode: 'embedded',
            target,
        },
        autostart: true,
        launch: {
            event: {
                type: 'launch',
                payload: {
                    player_id: Number(userId),
                    platform_type: 'web',
                    first_name: firstName,
                    last_name: lastName,
                    is_powered: isPowered,
                },
            },
        },
        voice: {
            url: VOICEFLOW_VOICE_URL,
        },
        // Voiceflow assistant styles
        // remove box-shadow and overflow: hidden from the assistant
        // hide the default title text (replaced via DOM injection in injectVoiceflowHeaderTitle)
        // .vfrc-chat {box-shadow: none !important; overflow: hidden !important;}.vfrc-header--title {font-size: 0 !important;}
        assistant: {
            stylesheet: 'data:text/css;base64,LnZmcmMtY2hhdCB7Ym94LXNoYWRvdzogbm9uZSAhaW1wb3J0YW50OyBvdmVyZmxvdzogaGlkZGVuICFpbXBvcnRhbnQ7fS52ZnJjLWhlYWRlci0tdGl0bGUge2ZvbnQtc2l6ZTogMCAhaW1wb3J0YW50O30=',
        },
    });

    if (window.voiceflow?.chat?.hide) {
        window.voiceflow.chat.hide();
    }

    injectVoiceflowHeaderTitle({ target });

    return injectVoiceflowHeaderCloseButton({
        target,
        onClose,
        existingObserver,
        setObserver,
    });
};
